import { HttpErrorResponse } from '@angular/common/http';
import { Job } from '@ats/models';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { Navigate } from '@ngxs/router-plugin';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { CompaniesState } from '@store/companies/companies.state';
import { FindJobs, GetJob, HandleErrors, InitStaticData, SetJob } from '@store/jobs/jobs.actions';
import { JobWebservice } from '@webservices/job-api/job.webservice';
import { buildUrl } from '@wizbii/angular-utilities';
import { of, throwError, zip } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export interface JobStateModel {
  jobs: Record<string, Job>;
  jobList: Record<string, Job[]>;
  domains: string[];
  experiences: string[];
  contracts: string[];
  durations: string[];
  studyLevels: string[];
  status: string[];

  error: any;
}

const defaultState: JobStateModel = {
  jobs: {},
  jobList: {},
  domains: [],
  experiences: [],
  contracts: [],
  durations: [],
  studyLevels: [],
  status: [],

  error: null,
};

export const NB_ITEMS_TO_DISPLAY = 20;

@State<JobStateModel>({
  name: 'jobs',
  defaults: defaultState,
})
export class JobsState {
  static job(jobId: string) {
    return createSelector(
      [JobsState],
      (state: JobStateModel) => {
        return state.jobs[jobId];
      }
    );
  }

  static list(companyId: string, page: number) {
    return createSelector(
      [JobsState],
      (state: JobStateModel) => {
        return state.jobList[companyId].slice(0, page * NB_ITEMS_TO_DISPLAY);
      }
    );
  }

  static listLength(companyId: string) {
    return createSelector(
      [JobsState],
      (state: JobStateModel) => {
        return (state.jobList[companyId] || []).length;
      }
    );
  }

  @Selector()
  static domains(state: JobStateModel) {
    return state.domains;
  }

  @Selector()
  static contracts(state: JobStateModel) {
    return state.contracts;
  }

  @Selector()
  static durations(state: JobStateModel) {
    return state.durations;
  }

  @Selector()
  static studyLevels(state: JobStateModel) {
    return state.studyLevels;
  }

  @Selector()
  static status(state: JobStateModel) {
    return state.status;
  }

  @Selector()
  static experiences(state: JobStateModel) {
    return state.experiences;
  }

  @Selector()
  static error(state: JobStateModel) {
    return state.error;
  }

  constructor(private readonly jobWebservice: JobWebservice, private readonly store: Store) {}

  @Action(InitStaticData)
  initStaticData(ctx: StateContext<JobStateModel>) {
    const { domains, contracts, durations, status, studyLevels, experiences } = ctx.getState();

    if (
      domains.length > 0 &&
      contracts.length > 0 &&
      durations.length > 0 &&
      status.length > 0 &&
      studyLevels.length > 0 &&
      experiences.length > 0
    ) {
      return undefined;
    }

    return zip(
      this.jobWebservice.getDomains(),
      this.jobWebservice.getContracts(),
      this.jobWebservice.getDurations(),
      this.jobWebservice.getStudyLevels(),
      this.jobWebservice.getStatus(),
      this.jobWebservice.getExperiences()
    ).pipe(
      switchMap(([_domains, _contracts, _durations, _studyLevels, _status, _experiences]) =>
        of(
          ctx.patchState({
            domains: _domains,
            contracts: _contracts,
            durations: _durations,
            studyLevels: _studyLevels,
            status: _status,
            experiences: _experiences,
          })
        )
      ),
      catchError((err: HttpErrorResponse | Error) => {
        if (err instanceof HttpErrorResponse && err.status === 500) {
          return throwError(new Error('Internal Server Error while getting datas'));
        }

        return throwError(err);
      })
    );
  }

  @Action(SetJob)
  setJob(ctx: StateContext<JobStateModel>, { job }: SetJob) {
    return ctx.setState(
      patch<JobStateModel>({
        jobs: patch({ [job._id]: job }),
      })
    );
  }

  @Action(GetJob)
  getJob(ctx: StateContext<JobStateModel>, { jobId }: GetJob) {
    const { jobs } = ctx.getState();

    if (!jobs[jobId]) {
      ctx.patchState({ error: null });

      return this.jobWebservice.get(jobId).pipe(
        switchMap(newJob => {
          if (newJob.companyId !== this.store.selectSnapshot(CompaniesState.authenticatedCompanyId)) {
            console.error(`Not found - job.companyId !== authenticatedCompany.id [jobId=${jobId}]`);
            return ctx.dispatch(
              new Navigate([buildUrl(FeaturesRoutingEnum.NotFound)], undefined, { skipLocationChange: true })
            );
          }
          return ctx.dispatch(new SetJob(newJob));
        }),
        catchError(error => {
          switch (error.status) {
            // The Job doesn't exist so => 404
            case 404: {
              console.error(`Not found - [jobId=${jobId}]`);
              ctx.patchState({ error });
              return ctx.dispatch(
                new Navigate([buildUrl(FeaturesRoutingEnum.NotFound)], undefined, { skipLocationChange: true })
              );
            }

            // We are not authorized to access this job => 403
            case 403: {
              console.error(`Forbidden - [jobId=${jobId}]`);
              ctx.patchState({ error });
              return ctx.dispatch(new Navigate(['/'], undefined));
            }

            default: {
              console.error(`Code ${error.status} => ${error.statusText} - [jobId=${jobId}]`);
              return of(ctx.patchState({ error }));
            }
          }
        })
      );
    }
  }

  @Action(FindJobs)
  getAllJob(ctx: StateContext<JobStateModel>, { companyId }: FindJobs) {
    return this.jobWebservice.find(companyId).pipe(
      map(jobList =>
        ctx.setState(
          patch<JobStateModel>({
            jobList: patch({ [companyId]: jobList }),
          })
        )
      ),
      // tslint:disable-next-line:no-identical-functions
      catchError(error => ctx.dispatch(new HandleErrors(error)))
    );
  }

  @Action(HandleErrors)
  handleErrors(ctx: StateContext<JobStateModel>, { error }: HandleErrors) {
    // tslint:disable no-small-switch
    switch (error.status) {
      // The Job doesn't exist so => 404
      case 404: {
        console.error('Not found - [jobs]');

        ctx.patchState({ error });
        return ctx.dispatch(
          new Navigate([buildUrl(FeaturesRoutingEnum.NotFound)], undefined, { skipLocationChange: true })
        );
      }

      default: {
        console.error(`Code ${error.status} => ${error.statusText}  - [jobs]`);
        return ctx.patchState({ error });
      }
    }
    // tslint:enable no-small-switch
  }
}
